import React, { useMemo } from 'react';
import cn from 'clsx';
import { Layout } from 'fronton-react';
import { createElementKey } from 'utils';
import { ButtonWithGAClickButton } from '../button';
import { Section } from '../section';
import { StickyPanel } from '../sticky-panel';
import { Typography } from '../typography';
import { PanelActionBlock } from './components/mobile-fixed-panel';
import { MobileFixedPanelCreator } from './components/mobile-fixed-panel-creator';
import { PanelBlocks } from './design-registration-panel-enums';
import {
  bannerIcon,
  designRegistrationPanelSection,
  fixedBannerIcon,
  fixedBannerWrapper,
  fixedButton,
  fixedClassName,
  fixedStickyBannerDescription,
  fixedTextBlock,
  flexRowBannerWrapper,
  hiddenBannerIcon,
  hiddenStickyBanner,
  newstickyBannerDescription,
  newTextBlock,
  stickyBanner,
  stickyBannerActionWrapper,
  stickyBannerDescription,
  stickyBannerWrapper,
  textBlock,
} from './design-registration-panel-styles';
import {
  BaseDesignRegistrationPanelProps,
  DesignRegistrationPanelStructure,
} from './design-registration-panel-types';

export const DEFAULT_PANEL_TEXT =
  'Запишитесь на\u00A0проектирование или создайте свой проект самостоятельно';

type DesignRegistrationPanelProps = {
  className?: string;
  newVersion?: boolean;
  structure: DesignRegistrationPanelStructure;
} & Pick<BaseDesignRegistrationPanelProps, 'isConsultationFormVisible' | 'withWhiteBackground'>;

export const DesignRegistrationPanel: React.FC<DesignRegistrationPanelProps> = ({
  className,
  isConsultationFormVisible,
  withWhiteBackground,
  structure,
  newVersion = false,
}) => {
  const { content, actionBlock } = useMemo(() => structure[PanelBlocks.Panel], [structure]);
  const PanelIcon = content.leftIcon;

  return (
    <Section className={cn(designRegistrationPanelSection, className)}>
      <StickyPanel
        className={cn(stickyBanner, { [hiddenStickyBanner]: isConsultationFormVisible })}
        fixedClassName={fixedClassName}
        renderContent={(fixed: boolean) => (
          <Layout className={cn(stickyBannerWrapper, { [fixedBannerWrapper]: fixed, [flexRowBannerWrapper]: newVersion })}>
            <div className={cn(textBlock, { [fixedTextBlock]: fixed, [newTextBlock]: newVersion })}>
              <PanelIcon
                className={cn(bannerIcon, { [fixedBannerIcon]: fixed, [hiddenBannerIcon]: newVersion })}
                color={content.iconColor}
                size="xxl"
                strokeWidth={content.iconStrokeWidth ?? 0.5}
                type={content.iconType}
              />
              <Typography
                adaptive={false}
                className={cn(
                  stickyBannerDescription,
                  { [fixedStickyBannerDescription]: fixed, [newstickyBannerDescription]: newVersion }
                )}
                color="text-invert"
                size="body_short"
                variant="m">
                {fixed && content.shortText ? content.shortText : content.text}
              </Typography>
            </div>
            <div className={stickyBannerActionWrapper}>
              {actionBlock.map(({ label, onClick, shortLabel, ...btnProps }, index: number) => {
                const btnLabel = fixed && shortLabel ? shortLabel : label;
                const newElemName = fixed ? 'sticky-panel' : 'startContent';

                return (
                  <ButtonWithGAClickButton
                    adaptive={false}
                    aria-label={label}
                    className={cn({ [fixedButton]: fixed })}
                    elementName={newElemName}
                    elementText={btnLabel}
                    onClick={onClick}
                    key={createElementKey('panel-btn', index)}
                    title={btnLabel}
                    {...(btnProps as Omit<PanelActionBlock, 'label' | 'onClick'>)}>
                    {btnLabel}
                  </ButtonWithGAClickButton>
                );
              })}
            </div>
          </Layout>
        )}
      />

      <MobileFixedPanelCreator
        isConsultationFormVisible={isConsultationFormVisible}
        withWhiteBackground={withWhiteBackground}
        structure={structure[PanelBlocks.MobilePanel]}
        tabletActionBloc={structure[PanelBlocks.Panel].actionBlock}
      />
    </Section>
  );
};
